import {
	DustPortfolios,
	PortfolioAssetDetails,
	PortfolioOverview,
	PortfolioOverviewAssetGroup
} from "@modules/portfolio/domain/entities/portfolio-overview.model";
import {
	COIN_TYPE,
	CRYPTO_TYPE,
	FIAT_TYPE,
	INDEX_TYPE,
	LEVERAGED_TOKEN_TYPE,
	MULTI_SWAP_MAX_LENGTH,
	PORTFOLIO_MAX_ITEMS_TO_DISPLAY,
	PORTFOLIO_ZERO_VALUE_FOR_STRINGS_NUMBERS,
	TOKEN_BEST_ID,
	TRADABLES_TYPE
} from "@helpers/constants";
import { Currency } from "@models/interfaces/currency";
import {
	PortfolioAssetGroupProjection,
	PortfolioAssetProjection,
	PortfolioData
} from "@modules/portfolio/domain/entities/portfolio-projection.model";
import { PortfolioOverviewState } from "@modules/portfolio/+state/portfolio-overview.state.service";
import { CashPlusStateCombined } from "@modules/cashplus/+state/cashplus.facade";
import { BpOption } from "@models/ui-components/bp-option";
import { Timespan } from "@helpers/const/timespan";
import { Point } from "@models/point";
import { ProfileModel } from "@modules/main/components/profile/new-profile/entities/profile-model";
import { PortfolioPerformanceState } from "@modules/portfolio/+state/portfolio-performance.state.service";
import { Fiat } from "@models";
import { toEuroByFiatId } from "@helpers/tradable-convert";
import { MultiSwapAcceptResponse, MultiSwapOfferRequest } from "@gql/offer";
import { Tradable } from "@models/interfaces/tradable";
import { PortfolioMarginPositionsPageFragment } from "../../../api/graphql/bff/generated/fragments.service.graphql-gen";

export function sortAssets(performance, defaultFiat): PortfolioPerformanceState {
	// Sort array desc by asset_group
	// Sort fiat asset group always to the bottom
	return {
		...performance,
		asset_group_performances: (performance?.asset_group_performances ?? [])
			.sort((a, b) => (a.asset_group < b.asset_group ? 1 : -1))
			.sort((a, _) => (a.asset_group === defaultFiat.getCurrencyType() ? 1 : -1)),
		error: false,
		isLoading: false
	};
}

export function groupAssetsPerGroup(overview: PortfolioOverview): PortfolioOverviewAssetGroup[] {
	let dict = [];
	for (const val of overview.portfolios) {
		dict[val.asset_group] = [...(dict[val.asset_group] ?? []), val];
	}

	return Object.keys(dict).map((val) => ({ asset_group: val, assets: dict[val] }));
}

export function getAssetGroupName(assetGroup: string): string {
	const lowerSectionType = assetGroup.toLowerCase();
	const isCrypto = lowerSectionType === CRYPTO_TYPE.toLowerCase() || lowerSectionType === COIN_TYPE.toLowerCase();

	return isCrypto ? CRYPTO_TYPE.toLowerCase() : lowerSectionType;
}

export function getGroupType(type: string): string {
	return type === CRYPTO_TYPE ? COIN_TYPE : type;
}

export function isSameCurrency(item: PortfolioAssetDetails, currency: Currency): boolean {
	return item.asset_id === currency.getId() && item.asset_group === getGroupType(currency.getCurrencyType());
}

export function getAssetsWidgetPortfolio(assetsGroup): PortfolioAssetProjection[] {
	return assetsGroup
		.flatMap((group) => group.assets)
		.filter((val) => !val.isFiat)
		.sort((a, b) => (+a.fiatBalance > +b.fiatBalance ? -1 : 1))
		.slice(0, PORTFOLIO_MAX_ITEMS_TO_DISPLAY);
}

export function getEmptyPortfolioWidgetAndPageData(
	overview,
	performance,
	isGroupedEnabled: boolean,
	profile?: ProfileModel
): PortfolioData {
	return {
		projection: {
			currentValue: PORTFOLIO_ZERO_VALUE_FOR_STRINGS_NUMBERS,
			returnPercentage: PORTFOLIO_ZERO_VALUE_FOR_STRINGS_NUMBERS,
			groups: []
		},
		dataPoints: [],
		isGroupedPerformanceActive: isGroupedEnabled,
		cashPlus: {
			configurations: null,
			subscriptions: null,
			isCashPlusEnabled: false
		},
		isLoading: performance.isLoading || overview.isLoading,
		apiError: (!performance.isLoading && performance.error) || (!overview.isLoading && overview.error),
		languageSelected: null,
		profileVM: profile,
		timespanOptions: null,
		isHideReturnKpisEnabled: false,
		assets: null
	};
}

export function returnPortfolioWidgetData(
	overview: PortfolioOverviewState,
	performance: PortfolioPerformanceState,
	isGroupedEnabled: boolean,
	assetsGroup: PortfolioAssetGroupProjection[],
	profile: ProfileModel,
	language: string,
	timespan: BpOption<string, Timespan>[],
	assets: PortfolioAssetProjection[],
	isPortfolioHidden: boolean
): PortfolioData {
	return {
		projection: {
			currentValue: performance?.overall_portfolio_current_value ?? PORTFOLIO_ZERO_VALUE_FOR_STRINGS_NUMBERS,
			returnPercentage: performance?.return_percentage ?? PORTFOLIO_ZERO_VALUE_FOR_STRINGS_NUMBERS,
			groups: assetsGroup
		},
		dataPoints: performance.data?.map((c) => new Point(new Date(c.time).valueOf(), +c.value)),
		isGroupedPerformanceActive: isGroupedEnabled,
		isLoading: performance.isLoading || overview.isLoading,
		apiError: (!performance.isLoading && performance.error) || (!overview.isLoading && overview.error),
		profileVM: profile,
		languageSelected: language,
		timespanOptions: timespan,
		assets: assets,
		isHidePortfolioValueActive: isPortfolioHidden
	};
}

export function returnPortfolioPageData(
	overview: PortfolioOverviewState,
	performance: PortfolioPerformanceState,
	isGroupedEnabled: boolean,
	assetsGroup: PortfolioAssetGroupProjection[],
	cashPlus: CashPlusStateCombined,
	language: string,
	timespan: BpOption<string, Timespan>[],
	isKpisEnabled: boolean,
	showCrossSelling: boolean,
	shouldHighlightAllocation: boolean,
	hidePortfolioValue: boolean,
	isDustToBestEnabled: boolean,
	assetsDust: DustPortfolios[],
	dustAssetType: string[],
	hasDust: boolean,
	marginTradingPositions: PortfolioMarginPositionsPageFragment
): PortfolioData {
	return {
		projection: {
			currentValue: performance.overall_portfolio_current_value ?? PORTFOLIO_ZERO_VALUE_FOR_STRINGS_NUMBERS,
			returnPercentage: performance.return_percentage ?? PORTFOLIO_ZERO_VALUE_FOR_STRINGS_NUMBERS,
			groups: assetsGroup
		},
		dataPoints: performance.data?.map((c) => new Point(new Date(c.time).valueOf(), +c.value)),
		isGroupedPerformanceActive: isGroupedEnabled,
		cashPlus: {
			subscriptions: cashPlus?.subscriptions,
			configurations: cashPlus?.configurations,
			isCashPlusEnabled: cashPlus?.isCashPlusEnabled
		},
		isLoading: performance.isLoading || overview.isLoading,
		apiError: (!performance.isLoading && performance.error) || (!overview.isLoading && overview.error),
		languageSelected: language,
		timespanOptions: timespan,
		isHideReturnKpisEnabled: isKpisEnabled,
		showCrossSelling,
		shouldShowAllocation: !(assetsGroup.length === 1 && assetsGroup[0].isFiat),
		shouldHighlightAllocation,
		isHidePortfolioValueActive: hidePortfolioValue,
		isDustToBestEnabled,
		assetsDust,
		dustAssetType,
		hasDust,
		marginTradingPositions
	};
}

// Dust Portfolios
export function deserializeDustWallet(
	dustPortfolios: PortfolioAssetDetails[],
	defaultFiat: Fiat,
	bestPrice: number
): DustPortfolios[] {
	return dustPortfolios.map((portfolio, index) => {
		return {
			...portfolio,
			isChecked: index < MULTI_SWAP_MAX_LENGTH,
			bestQuantity: toEuroByFiatId(+portfolio.fiat_balance, defaultFiat) / bestPrice
		};
	});
}

export function deserializeMultiSwapAccept(input: MultiSwapAcceptResponse): string {
	if (input["__typename"] === "MultiSwapSuccessResponse") {
		return input.multiSwap.swapId;
	}

	if (input["__typename"] === "SwapInProgressResponse") {
		return input.swapInProgress.swapId;
	}
}

export function deserializeDustToPayload(dust: DustPortfolios[], fiatId: string): MultiSwapOfferRequest {
	return {
		multiSwapOffer: dust.map((asset) => {
			return {
				sourceAssetId: asset.asset_id,
				sourceAssetAmount: { value: asset.asset_balance }
			};
		}),
		fiatId
	};
}

export function filterDustWallets(
	wallets: PortfolioAssetDetails[],
	tradableList: Tradable[],
	defaultFiat: Fiat
): PortfolioAssetDetails[] {
	return wallets.filter((wallet) => {
		return tradableList.find(
			(tradable) =>
				wallet.asset_group !== TRADABLES_TYPE.SECURITY_TOKEN &&
				wallet.asset_group !== LEVERAGED_TOKEN_TYPE &&
				wallet.asset_group !== INDEX_TYPE &&
				+wallet.staked_balance_percent === 0 &&
				wallet.asset_group !== FIAT_TYPE &&
				wallet.asset_id !== TOKEN_BEST_ID &&
				tradable.getId() === wallet.asset_id &&
				tradable.isSellActive() &&
				toEuroByFiatId(+wallet.fiat_balance, defaultFiat) < tradable.getMinSell()
		);
	});
}
